import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { X, Trash2, Check, Plus } from "lucide-react";

import { theme } from "./theme";
import { apiFetch } from "./api";
import { formatDateTime } from "./dateFormat";
import { useT } from "./i18n";

const ACTING_NAME_KEY = "frontdesk_acting_name";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(26, 26, 26, 0.5);
  backdrop-filter: blur(2px);
`;

const Panel = styled.div`
  width: 100%;
  max-width: 460px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 14px;
  border-top: 3px solid ${theme.gold};
  box-shadow: 0 24px 60px -18px rgba(26, 26, 26, 0.5);
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.9rem 1rem 0.6rem;
  border-bottom: 1px solid ${theme.border};
`;

const Title = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const Sub = styled.div`
  font-size: 0.75rem;
  color: ${theme.muted};
  margin-top: 0.15rem;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const List = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.5rem 1rem;
`;

const Empty = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: ${theme.muted};
  font-size: 0.85rem;
`;

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid ${theme.border};
`;

const Toggle = styled.button`
  flex: 0 0 auto;
  width: 1.3rem;
  height: 1.3rem;
  margin-top: 0.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1.5px solid ${(props) => (props.$checked ? "#157347" : theme.border)};
  background: ${(props) => (props.$checked ? "#157347" : "#ffffff")};
  color: #ffffff;
  cursor: pointer;
`;

const Body = styled.div`
  flex: 1 1 auto;
`;

const Text = styled.div`
  font-size: 0.9rem;
  color: ${theme.ink};
  text-decoration: ${(props) => (props.$checked ? "line-through" : "none")};
  opacity: ${(props) => (props.$checked ? 0.6 : 1)};
  word-break: break-word;
`;

const Meta = styled.div`
  font-size: 0.75rem;
  color: ${theme.muted};
  margin-top: 0.15rem;
`;

const DeleteButton = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.7rem;
  height: 1.7rem;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: ${theme.muted};
  cursor: pointer;
  &:hover {
    background: #fdecec;
    color: #c0392b;
  }
`;

const Form = styled.div`
  padding: 0.75rem 1rem;
  border-top: 1px solid ${theme.border};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const NameInput = styled.input`
  padding: 0.4rem 0.55rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.82rem;
`;

const Textarea = styled.textarea`
  padding: 0.5rem 0.55rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.88rem;
  resize: vertical;
  min-height: 3rem;
  font-family: inherit;
`;

const AddButton = styled.button`
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.9rem;
  border: 0;
  border-radius: 8px;
  background: ${theme.ink};
  color: #ffffff;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FoCommentsModal = (props) => {
  const { t, lang } = useT();
  const line = props.line;
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [name, setName] = useState(
    () =>
      (typeof localStorage !== "undefined" &&
        localStorage.getItem(ACTING_NAME_KEY)) ||
      "",
  );
  const [busy, setBusy] = useState(false);
  // True once anything was added/toggled/deleted — onClose(dirty) lets the page refresh its badges.
  const dirtyRef = useRef(false);

  const close = () => props.onClose(dirtyRef.current);

  const applyData = (data) => {
    dirtyRef.current = true;
    if (data && Array.isArray(data.items)) {
      setItems(data.items);
      if (props.onCount) {
        props.onCount(line.id, data.count);
      }
    }
  };

  useEffect(() => {
    let active = true;
    apiFetch(`/frontdesk_v2/lines/${line.id}/comments`).then((res) => {
      if (active && res?.data && Array.isArray(res.data.items)) {
        setItems(res.data.items);
      }
    });
    return () => {
      active = false;
    };
  }, [line.id]);

  const persistName = (value) => {
    setName(value);
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(ACTING_NAME_KEY, value);
    }
  };

  const add = async () => {
    if (!name.trim()) {
      window.alert(t("fo_name_required"));
      return;
    }
    if (!text.trim() || busy) {
      return;
    }
    setBusy(true);
    const res = await apiFetch(`/frontdesk_v2/lines/${line.id}/comments`, {
      method: "POST",
      body: JSON.stringify({
        description: text.trim(),
        created_by_name: name.trim(),
      }),
    });
    applyData(res.data);
    setText("");
    setBusy(false);
  };

  const toggle = async (item) => {
    const res = await apiFetch(`/frontdesk_v2/comments/${item.id}`, {
      method: "PATCH",
      body: JSON.stringify({
        toggle: item.checked ? "uncheck" : "check",
        acting_name: name.trim() || "FO",
      }),
    });
    applyData(res.data);
  };

  const remove = async (item) => {
    const res = await apiFetch(`/frontdesk_v2/comments/${item.id}`, {
      method: "DELETE",
    });
    applyData(res.data);
  };

  return (
    <Overlay onClick={close}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <Head>
          <div>
            <Title>{t("fo_comments_title")}</Title>
            <Sub>
              {line.room_code ? `${line.room_code} · ` : ""}
              {line.guest_name || t("fo_comments_hint")}
            </Sub>
          </div>
          <IconButton type="button" aria-label={t("close")} onClick={close}>
            <X size={16} />
          </IconButton>
        </Head>

        <List>
          {items.length === 0 ? (
            <Empty>{t("fo_empty")}</Empty>
          ) : (
            items.map((item) => (
              <Item key={item.id}>
                <Toggle
                  type="button"
                  $checked={item.checked}
                  aria-label={t("fo_checked")}
                  onClick={() => toggle(item)}
                >
                  {item.checked ? <Check size={13} /> : null}
                </Toggle>
                <Body>
                  <Text $checked={item.checked}>{item.description}</Text>
                  <Meta>
                    {item.created_by_name} ·{" "}
                    {formatDateTime(item.created_at, lang)}
                  </Meta>
                </Body>
                <DeleteButton
                  type="button"
                  aria-label={t("fo_delete")}
                  onClick={() => remove(item)}
                >
                  <Trash2 size={15} />
                </DeleteButton>
              </Item>
            ))
          )}
        </List>

        <Form>
          <NameInput
            type="text"
            placeholder={t("note_author")}
            value={name}
            onChange={(event) => persistName(event.target.value)}
          />
          <Textarea
            placeholder={t("fo_add_placeholder")}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
          <AddButton
            type="button"
            disabled={busy || !text.trim()}
            onClick={add}
          >
            <Plus size={15} />
            {t("fo_add_btn")}
          </AddButton>
        </Form>
      </Panel>
    </Overlay>
  );
};

export default FoCommentsModal;
